import React from 'react';
import { VehiculeConfig, Service, PersonnelConfig, Competence } from '../../../../../../shared/types/config';

interface VehiculesTabProps {
  vehiculeConfigs: VehiculeConfig[];
  searchVehicule: string;
  onSearchChange: (value: string) => void;
  onAdd: () => void;
  onEdit: (vehicule: VehiculeConfig) => void;
  onDelete: (id: number) => void;
  services: Service[];
  personnelConfigs: PersonnelConfig[];
  competences: Competence[];
}

const VehiculesTab: React.FC<VehiculesTabProps> = ({
  vehiculeConfigs,
  searchVehicule,
  onSearchChange,
  onAdd,
  onEdit,
  onDelete,
  services,
  personnelConfigs,
  competences,
}) => {
  const filteredVehicules = vehiculeConfigs.filter(
    (v) =>
      v.nom.toLowerCase().includes(searchVehicule.toLowerCase()) ||
      v.type.toLowerCase().includes(searchVehicule.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder="Rechercher par nom ou type..."
            value={searchVehicule}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full rounded-lg border border-[#223046] bg-[#0b1220] px-3 py-2 text-sm text-cockpit-text placeholder-cockpit-muted focus:outline-none focus:border-[#6366f1]/60"
          />
          <svg
            className="w-5 h-5 absolute right-3 top-1/2 transform -translate-y-1/2 text-cockpit-muted"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <button
          onClick={onAdd}
          className="rounded-lg border border-[#6366f1]/40 bg-[#6366f1]/20 px-4 py-2 text-sm font-semibold text-[#c7d2fe] hover:bg-[#6366f1]/30 transition-colors flex items-center gap-2 shrink-0"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Ajouter
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="border-b border-[#1a2537]">
            <tr>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">ID</th>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Nom</th>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Type</th>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Niveaux</th>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Prix</th>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Service</th>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Armement</th>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Priorité grades</th>
              <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredVehicules.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-cockpit-text-2">
                  Aucun véhicule trouvé
                </td>
              </tr>
            ) : (
              filteredVehicules.map((vehicule) => {
                const vid =
                  Number(vehicule.id_service) ||
                  Number((vehicule as VehiculeConfig & { service_id?: number }).service_id) ||
                  0;
                const service = services.find((s) => Number(s.id) === vid);
                const prioIds =
                  vehicule.id_grades_priorite?.length ? vehicule.id_grades_priorite : vehicule.id_grades_armement ?? [];
                const gradeLabelsOrdered = prioIds
                  .map((gid) => personnelConfigs.find((p) => p.id === gid)?.grade)
                  .filter(Boolean) as string[];
                const compIds = vehicule.id_competences_armement ?? [];
                const compLabels = compIds.map((cid) => {
                  const c = competences.find((x) => x.id === cid);
                  if (!c) return `ID ${cid}`;
                  return c.is_specialite ? `${c.nom} (spé)` : c.nom;
                });
                return (
                  <tr key={vehicule.id} className="border-b border-[#1a2537]/60 hover:bg-[#0b1220]/40 transition-colors">
                    <td className="px-4 py-3 text-cockpit-text-2">{vehicule.id}</td>
                    <td className="px-4 py-3 text-cockpit-text font-medium">{vehicule.nom}</td>
                    <td className="px-4 py-3 text-cockpit-text-2">{vehicule.type}</td>
                    <td className="px-4 py-3 text-cockpit-text-2">{vehicule.niveaux}</td>
                    <td className="px-4 py-3 text-cockpit-text-2">{vehicule.prix.toLocaleString()} €</td>
                    <td className="px-4 py-3 text-cockpit-text-2">{service?.nom || (vid ? `ID ${vid}` : '—')}</td>
                    <td className="px-4 py-3">
                      {compLabels.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {compLabels.map((nom, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-emerald-500/20 text-emerald-200 rounded text-xs border border-emerald-500/40"
                            >
                              {nom}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-cockpit-muted text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm max-w-[14rem]">
                      {gradeLabelsOrdered.length > 0 ? (
                        <span className="text-xs text-cockpit-text-2">
                          {gradeLabelsOrdered.join(' → ')}
                        </span>
                      ) : (
                        <span className="text-cockpit-muted text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onEdit(vehicule)}
                          className="rounded border border-[#6366f1]/40 bg-[#6366f1]/20 p-1.5 text-[#c7d2fe] hover:bg-[#6366f1]/30 transition-colors"
                          title="Modifier"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => vehicule.id && onDelete(vehicule.id)}
                          className="rounded border border-red-500/35 bg-red-500/10 p-1.5 text-red-300 hover:bg-red-500/20 transition-colors"
                          title="Supprimer"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VehiculesTab;
